/**
 * A master document's children, and what state the set is in.
 *
 * Three things this panel is careful about:
 *
 *  - The status chip is an OUTLINE chip, visually distinct from the filled
 *    lifecycle chip beside it. `content_status` is derived and advisory; it is
 *    not a lifecycle state and must never be read as one.
 *  - Hidden children are shown as a COUNT with no titles. Naming a document
 *    somebody may not see confirms it exists.
 *  - Re-ordering writes a MIDPOINT order value rather than renumbering the
 *    siblings, which is why the column is a float.
 */
import React from 'react'

import type {
  DmsChild, DmsClient, DmsCompound, DmsBindingMode,
} from './contracts'
import {
  BINDING_HELP, BINDING_LABELS, CONTENT_STATUS_LABELS, CONTENT_STATUS_TONE,
} from './contracts'

const TONE_COLOR: Record<string, string> = {
  ok: 'var(--success, #1a7f37)',
  warn: 'var(--warning, #9a6700)',
  bad: 'var(--danger, #b42318)',
  muted: 'var(--text-muted, #667085)',
}

export interface CompoundPanelProps {
  compound: DmsCompound
  client: DmsClient
  documentId: string
  canEdit: boolean
  onChanged?: () => void
}

export function CompoundPanel({
  compound, client, documentId, canEdit, onChanged,
}: CompoundPanelProps) {
  const [busy, setBusy] = React.useState('')
  const [error, setError] = React.useState('')

  if (!compound || (!compound.is_master && !compound.children.length
                    && !compound.hidden_children)) {
    return null
  }

  const tone = TONE_COLOR[CONTENT_STATUS_TONE[compound.content_status] || 'muted']

  async function run(label: string, fn: () => Promise<unknown>) {
    setBusy(label)
    setError('')
    try {
      await fn()
      onChanged?.()
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail
      setError(detail || 'That could not be done.')
    } finally {
      setBusy('')
    }
  }

  async function rebind(child: DmsChild, mode: DmsBindingMode) {
    if (!client.updateChild) return
    await run(child.edge_id,
              () => client.updateChild!(child.edge_id, { binding_mode: mode }))
  }

  async function move(index: number, delta: number) {
    if (!client.reorderChildren) return
    const next = [...compound.children]
    const target = index + delta
    if (target < 0 || target >= next.length) return
    ;[next[index], next[target]] = [next[target], next[index]]
    await run('order',
              () => client.reorderChildren!(documentId,
                                            next.map((c) => c.edge_id)))
  }

  return (
    <section className="dms-compound" style={{ marginTop: 18 }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <h3 style={{ margin: 0, fontSize: 14, textTransform: 'uppercase',
                     letterSpacing: 0.4 }}>
          Documents in this set
        </h3>
        <span
          title={compound.content_status_partial
            ? 'Calculated from the documents you can see.'
            : undefined}
          style={{
            fontSize: 13, padding: '1px 8px', borderRadius: 10,
            border: `1px solid ${tone}`, color: tone, background: 'transparent',
          }}
        >
          {CONTENT_STATUS_LABELS[compound.content_status]
           || compound.content_status}
          {compound.content_status_partial ? ' (partial)' : ''}
        </span>
      </header>

      {error && (
        <p style={{ color: TONE_COLOR.bad, fontSize: 13 }}>{error}</p>
      )}

      <ol style={{ listStyle: 'none', padding: 0, margin: '10px 0 0' }}>
        {compound.children.map((child, index) => (
          <li key={child.edge_id}
              style={{ padding: '8px 0',
                       borderTop: '1px solid var(--border, #e4e7ec)' }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
              {child.section_no && (
                <span style={{ fontVariantNumeric: 'tabular-nums',
                               color: 'var(--text-muted, #667085)' }}>
                  {child.section_no}
                </span>
              )}
              <strong style={{ flex: 1 }}>{child.label || child.title}</strong>
              <span style={{ fontSize: 13,
                             color: 'var(--text-muted, #667085)' }}>
                {child.version_label || '—'}
              </span>
            </div>

            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap',
                          marginTop: 4 }}>
              {child.broken && (
                <Badge tone="bad">Broken — {child.broken_reason}</Badge>
              )}
              {child.fell_back && (
                <Badge tone="warn">Showing the current version instead</Badge>
              )}
              {child.dirty && (
                <Badge tone="warn">
                  Out of date — {child.newer_label} is available
                </Badge>
              )}
              {!child.version_id && !child.broken && (
                <Badge tone="warn">{child.broken_reason || 'Not available'}</Badge>
              )}
              {!child.required && <Badge tone="muted">Optional</Badge>}
            </div>

            {canEdit && client.updateChild && (
              <div style={{ marginTop: 6, display: 'flex', gap: 8,
                            alignItems: 'center' }}>
                <select
                  value={child.binding_mode}
                  disabled={busy === child.edge_id}
                  title={BINDING_HELP[child.binding_mode]}
                  onChange={(e) =>
                    rebind(child, e.target.value as DmsBindingMode)}
                  style={{ fontSize: 13 }}
                >
                  {(Object.keys(BINDING_LABELS) as DmsBindingMode[]).map((m) => (
                    <option key={m} value={m}>{BINDING_LABELS[m]}</option>
                  ))}
                </select>
                {client.reorderChildren && (
                  <>
                    <button type="button" onClick={() => move(index, -1)}
                            disabled={index === 0 || !!busy}>↑</button>
                    <button type="button" onClick={() => move(index, 1)}
                            disabled={index === compound.children.length - 1
                                      || !!busy}>↓</button>
                  </>
                )}
                {client.removeChild && (
                  <button type="button" disabled={!!busy}
                          onClick={() => run(child.edge_id,
                                             () => client.removeChild!(child.edge_id))}>
                    Remove
                  </button>
                )}
              </div>
            )}
          </li>
        ))}
      </ol>

      {compound.hidden_children > 0 && (
        // A count, never a list. See the note at the top of the file.
        <p style={{ fontSize: 13, color: 'var(--text-muted, #667085)',
                    marginTop: 8 }}>
          {compound.hidden_children} further document
          {compound.hidden_children === 1 ? ' is' : 's are'} in this set that you
          are not able to view.
        </p>
      )}

      {compound.snapshot && (
        <p style={{ fontSize: 13, color: 'var(--text-muted, #667085)',
                    marginTop: 8 }}>
          Baseline captured — {compound.snapshot.item_count} document
          {compound.snapshot.item_count === 1 ? '' : 's'}, set&nbsp;
          <code style={{ fontSize: 13 }}>
            {compound.snapshot.manifest_sha256.slice(0, 12)}
          </code>
        </p>
      )}
    </section>
  )
}

function Badge({ tone, children }:
               { tone: keyof typeof TONE_COLOR; children: React.ReactNode }) {
  return (
    <span style={{
      fontSize: 13, padding: '1px 7px', borderRadius: 9,
      border: `1px solid ${TONE_COLOR[tone]}`, color: TONE_COLOR[tone],
    }}>
      {children}
    </span>
  )
}

export default CompoundPanel
